/* eslint-disable import/prefer-default-export */
import { defaultMessage } from 'Components/Home/HomeServices';
import {
  aes256gcm,
  getJwkKeystore,
  getTransKey,
  jweEncryption,
  JWE_KEY,
} from 'Utils/crypt';
import { getDeviceId } from 'Utils/deviceUtils';
import {
  login, loginSucces, loginError, firstLogin,
} from '../../Shared/UserContext/Actions';
import api from '../../config/apiConfig';

// Key used to store device ID in localStorage
const DEVICE_ID_KEY = 'copima_device_id';

export const loginUser = async (dispatch, credential, history) => {
  try {
    // Get device ID BEFORE clearing localStorage (so we preserve it)
    const deviceId = getDeviceId();

    // Preserve all per-user tutorial timestamps and device ID before clearing localStorage
    const tutorialKeys = {};
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('tutorialLastShown_')) {
        tutorialKeys[key] = localStorage.getItem(key);
      }
    }
    // Clear localStorage to start fresh
    localStorage.clear();
    // Restore all tutorial timestamps
    Object.entries(tutorialKeys).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    // Restore device ID (important for MFA device trust feature)
    localStorage.setItem(DEVICE_ID_KEY, deviceId);

    const transKey = await getTransKey();
    const bodyFormData = new FormData();
    bodyFormData.set('username', credential.username);
    bodyFormData.set('password', credential.password);
    bodyFormData.set('transKey', transKey.encrypted);
    bodyFormData.set('deviceId', deviceId);
    bodyFormData.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    bodyFormData.set('language', navigator.language || '');
    bodyFormData.set('screenResolution', `${window.screen.width}x${window.screen.height}`);
    dispatch(login());

    const response = await api.post(
      '/login',
      bodyFormData,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      },
      false,
    );

    // Check for MFA requirement
    if (response.data.mfaRequired) {
      // Return MFA data for handling in Login component
      return {
        mfaRequired: true,
        nextStep: response.data.nextStep,
        token: response.data.token,
      };
    }

    const {
      data: { key, user, token },
    } = response;

    // Skip JWE decryption if key is null (JWE disabled)
    if (key && key !== null) {
      const aesCipher = await aes256gcm(Buffer.from(transKey.clair, 'hex'));

      // Backend encrypts 16-byte key with AES-GCM producing:
      // ciphertext (16 bytes = 32 hex) + authTag (16 bytes = 32 hex) = 64 hex chars total
      const encryptedData = key;

      // Split: first 32 hex chars = ciphertext, last 32 hex chars = authTag
      const ciphertext = encryptedData.slice(0, 32);
      const authTag = encryptedData.slice(32, 64);

      try {
        const keyJWE = await aesCipher.decrypt(
          Buffer.from(ciphertext, 'hex'),
          '0000000000000000',
          Buffer.from(authTag, 'hex'),
        );
        await localStorage.setItem(JWE_KEY, keyJWE);
      } catch (decryptError) {
        console.error('AES decryption failed:', decryptError);
        throw decryptError;
      }
    }

    // First login — redirect to update-password page
    if (user?.firstLogin) {
      dispatch(firstLogin(true, token, user));
      history.push('/update-password');
      return { success: true };
    }

    dispatch(loginSucces(user, token));
    if (user?.role === 'ROLE_ADMIN') {
      history.push('/users');
    } else {
      // Show tutorial popup once per day per account
      const tutorialKey = `tutorialLastShown_${user.username}`;
      const lastShownDate = localStorage.getItem(tutorialKey);
      const today = new Date().toDateString();
      const shouldShow = lastShownDate !== today;
      if (shouldShow) {
        sessionStorage.setItem('showTutorial', 'true');
      }
      history.push('/');
    }

    // Return success for normal login
    return { success: true };
  } catch (e) {
    // Extract error message safely
    let statusLabel = defaultMessage;
    try {
      statusLabel = e?.response?.data?.statusLabel || defaultMessage;
    } catch (extractError) {
      // If we can't extract the error, use default
      statusLabel = defaultMessage;
    }

    // Handle disabled user message
    if (statusLabel === 'User is disabled') {
      // Account manually disabled by admin (errors < 5)
      dispatch(loginError('Compte désactivé. Veuillez contacter votre administrateur.'));
      return { error: true, message: 'Compte désactivé. Veuillez contacter votre administrateur.' };
    }

    // Handle account disabled due to 5 failed login attempts
    if (statusLabel === '5 incorrect login attempts') {
      dispatch(loginError('5 tentatives de connexion incorrectes. Veuillez contacter votre administrateur.'));
      return { error: true, message: '5 tentatives de connexion incorrectes. Veuillez contacter votre administrateur.' };
    }

    // Handle wrong password (Bad credentials)
    if (statusLabel === 'Bad credentials') {
      dispatch(loginError('Mot de passe erroné. En cas d\'oublie, contactez-nous'));
      return { error: true, message: 'Mot de passe erroné. En cas d\'oublie, contactez-nous' };
    }

    // For ALL other errors
    dispatch(loginError('Mot de passe erroné. En cas d\'oublie, contactez-nous'));
    return { error: true, message: 'Mot de passe erroné. En cas d\'oublie, contactez-nous' };
  }
};

export const changeFirstPassword = async (dispatch, body, history, user, token) => {
  try {
    const conf = {
      headers: {
        Authorization: token,
      },
    };
    const key = await getJwkKeystore();
    const dataJWE = await jweEncryption(body, key);

    await api.post('/users/password/change', dataJWE, conf);

    dispatch(firstLogin(false));
    dispatch(loginSucces(user, token));
    if (user?.role === 'ROLE_ADMIN') {
      history.push('/users');
    } else {
      // Show tutorial popup once per day per account
      const tutorialKey = `tutorialLastShown_${user.username}`;
      const lastShownDate = localStorage.getItem(tutorialKey);
      const today = new Date().toDateString();
      const shouldShow = lastShownDate !== today;
      if (shouldShow) {
        sessionStorage.setItem('showTutorial', 'true');
      }
      history.push('/');
    }
  } catch (e) {
    const {
      response: {
        data: { statusLabel = defaultMessage },
      },
    } = e;
    dispatch(loginError(statusLabel));
  }
};
